import * as React from "react";
import { Row, Space, Dropdown, MenuProps } from "antd";
import { TableProps } from "antd/lib/table";
import { SearchBar } from "Components/General/SearchBar";
import { Button } from "Components/General/Button";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import i18n, { I18N_NS } from "Config/i18n";
import { ButtonProps } from "antd/lib/button";
import { DetailListActions, GroupedDropdownActionConfig } from "Components/General/DetailListActions";
import { useEffect, useState } from "react";
import { Table } from "Components/General/Table";
import { MenuItemType } from "antd/lib/menu/interface";
import { DownOutlined } from "@ant-design/icons";

export interface DataFilterTableProps<T> extends TableProps<T> {
  /**
   * Unique name of the table. Used to store the user's personal column widths in the localStorage.
   * Make sure to check if the name is unique, otherwise the error will be thrown.
   */
  name: string;
  dataSource: Array<T>;
  checkbox?: boolean;
  defaultSearchValue?: string;
  update?: (e: object) => void;
  placeholder?: string;
  onSearchChange?: (x: string) => void;
  searchOnChange?: boolean; // if set to "false" - handle search by pressing Enter or clicking on search icon
  onSearch?: (searchValue: string) => void;
  showSearch?: boolean;
  actionButtons?: {
    type?: ButtonProps["type"];
    disabled?: boolean;
    label: string;
    onClick: () => void;
    iconName: IconProp;
    loading?: boolean;
  }[];
  dropdownButton?: {
    menuItems: MenuItemType[];
    onClick: MenuProps["onClick"];
    label: React.ReactNode;
    faIcon?: IconProp;
  };
  filterPredicate?: (searchString: string) => (record: T) => boolean;
  dropdownActions?: GroupedDropdownActionConfig;
}

export const DataFilterTable = <T,>(props: React.PropsWithChildren<DataFilterTableProps<T>>) => {
  const {
    placeholder,
    actionButtons = [],
    dataSource = [],
    showSearch = true,
    searchOnChange = true,
    dropdownActions,
    defaultSearchValue,
    filterPredicate,
    onSearchChange,
    onSearch,
    children,
    dropdownButton,
  } = props;
  const [filter, setFilter] = useState<string>("");
  const [searchValue] = useState<string>(defaultSearchValue);

  const handleInputField = (value: string) => {
    if (!searchOnChange) {
      if (typeof onSearch !== "function") {
        throw new Error('Please provide "onSearch" function to DataFilterTable');
      }
      onSearch(value);
    } else {
      setFilter(value);
    }
  };

  const filteredData = () => {
    let filteredData = dataSource.slice();
    // use built-in filter only when no custom "onSearch" is defined
    if (filter !== "" && !onSearch) {
      if (filterPredicate) {
        return filteredData.filter(filterPredicate(filter));
      }
      filteredData = dataSource.filter((value: T) => {
        const check = Object.keys(value)
          .map((val) => {
            return value[val];
          })
          .filter((txt) => {
            return `${txt}`.toLowerCase().includes(filter.toLowerCase());
          });
        return check.length > 0;
      });
    }
    return filteredData;
  };

  useEffect(() => {
    if (defaultSearchValue) {
      setFilter(defaultSearchValue);
    }
  }, [defaultSearchValue]);

  return (
    <div>
      {showSearch && (
        <Row justify="space-between" style={{ flexWrap: "nowrap", alignItems: "flex-start" }} className="mb-2">
          {dropdownActions && (
            <div className="mr-md">
              <DetailListActions primaryActions={[]} groupedDropdownActions={dropdownActions} />
            </div>
          )}
          <SearchBar
            defaultValue={searchValue}
            searchOnChange={searchOnChange}
            placeholder={placeholder || i18n.t("Search", { ns: I18N_NS.GENERAL })}
            onSearch={handleInputField}
            onChange={onSearchChange}
            style={actionButtons.length ? { marginRight: 10, marginBottom: 0 } : undefined}
          />
          {!!dropdownButton && (
            <Dropdown
              menu={{
                items: dropdownButton.menuItems.map((item) => ({
                  key: item.key,
                  label: item.label,
                })),
                onClick: dropdownButton.onClick,
              }}
              trigger={["click"]}
            >
              <Button type="primary" className="ml-md" faIcon={dropdownButton.faIcon}>
                {dropdownButton.label} <DownOutlined />
              </Button>
            </Dropdown>
          )}
          {!!actionButtons.length && (
            <Space>
              {actionButtons.map((item, index) => (
                <Button
                  key={index}
                  faIcon={item.iconName}
                  loading={item.loading}
                  disabled={item.disabled}
                  type={item.type || "primary"}
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              ))}
            </Space>
          )}
        </Row>
      )}
      <Table
        {...props}
        name={props.name}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataSource={filteredData() as unknown as any[]}
      >
        {children}
      </Table>
    </div>
  );
};
